import React, { useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { genresAPI, colorsAPI, carosseriesAPI, energiesAPI, estadosAPI } from '../services/api';
import { Plus, Trash2, Tag, Palette, Car, Zap, Activity } from 'lucide-react';
import toast from 'react-hot-toast';

const SimpleCatalogManager = ({ api, label, itemKey, icon: Icon }) => {
    const [items, setItems] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [newValue, setNewValue] = React.useState('');

    const loadItems = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.list();
            setItems(res.data.data || []);
        } catch { toast.error(`Erreur de chargement (${label})`); }
        finally { setLoading(false); }
    }, [api, label]);

    React.useEffect(() => { loadItems(); }, [loadItems]);

    const handleAdd = async () => {
        if (!newValue.trim()) return;
        try {
            await api.create({ [itemKey]: newValue.trim() });
            toast.success(`${label} ajouté`);
            setNewValue('');
            loadItems();
        } catch { toast.error(`Erreur lors de l'ajout`); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Voulez-vous vraiment supprimer cet élément ?')) return;
        try {
            await api.delete(id);
            toast.success(`${label} supprimé`);
            loadItems();
        } catch { toast.error(`Erreur lors de la suppression`); }
    };

    return (
        <div className="card">
            <div className="form-section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon size={14} /> Gestion des {label}s
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <input
                    className="form-control"
                    placeholder={`Nouveau ${label}...`}
                    value={newValue}
                    onChange={e => setNewValue(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && handleAdd()}
                />
                <button className="btn btn-primary btn-sm" onClick={handleAdd}><Plus size={14} /> Ajouter</button>
            </div>
            {loading ? <div className="spinner" /> : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <div className="table-responsive">
                        <table className="table">
                            <thead>
                                <tr><th>Nom</th><th style={{ width: '40px' }}></th></tr>
                            </thead>
                            <tbody>
                                {Array.isArray(items) && items.map(item => (
                                    <tr key={item.id}>
                                        <td style={{ fontWeight: '500' }}>{item[itemKey] || '—'}</td>
                                        <td>
                                            <button className="btn btn-ghost btn-sm text-red" onClick={() => handleDelete(item.id)}>
                                                <Trash2 size={13} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {items.length === 0 && <p style={{ textAlign: 'center', padding: '10px', color: 'var(--text-muted)', fontSize: '13px' }}>Aucun élément trouvé</p>}
                </div>
            )}
        </div>
    );
};

const CataloguesPage = () => {
    const [catTab, setCatTab] = useState('genre');

    const subCatalogs = [
        { key: 'genre', label: 'Genres', icon: Tag },
        { key: 'color', label: 'Couleurs', icon: Palette },
        { key: 'carrosserie', label: 'Carrosseries', icon: Car },
        { key: 'energie', label: 'Énergies', icon: Zap },
        { key: 'estado', label: 'États', icon: Activity },
    ];

    return (
        <Layout title="Catalogues Techniques">
            <div className="animate-fade-in" style={{ animation: 'fadeIn 0.5s ease-out' }}>
                <div style={{ marginBottom: '20px' }}>
                    <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--primary-800)' }}>
                        📚 Catalogues Techniques
                    </h2>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                        Gérez les listes de référence pour les véhicules et sinistres.
                    </p>
                </div>

                <div className="grid-2" style={{ gap: '24px', alignItems: 'start' }}>
                    <div className="card" style={{ padding: '12px' }}>
                        <div style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '12px', padding: '0 8px' }}>
                            SÉLECTIONNER UN CATALOGUE
                        </div>
                        {subCatalogs.map(item => (
                            <div
                                key={item.key}
                                onClick={() => setCatTab(item.key)}
                                style={{
                                    padding: '12px 16px',
                                    cursor: 'pointer',
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: '14px',
                                    fontWeight: catTab === item.key ? '700' : '500',
                                    background: catTab === item.key ? 'var(--primary-50)' : 'transparent',
                                    color: catTab === item.key ? 'var(--primary-700)' : 'var(--text-secondary)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    marginBottom: '4px',
                                    transition: 'var(--transition)'
                                }}
                            >
                                <item.icon size={16} /> {item.label}
                            </div>
                        ))}
                    </div>

                    <div>
                        {catTab === 'genre' && <SimpleCatalogManager api={genresAPI} label="Genre" itemKey="genre" icon={Tag} />}
                        {catTab === 'color' && <SimpleCatalogManager api={colorsAPI} label="Couleur" itemKey="color" icon={Palette} />}
                        {catTab === 'carrosserie' && <SimpleCatalogManager api={carosseriesAPI} label="Carrosserie" itemKey="carosserie" icon={Car} />}
                        {catTab === 'energie' && <SimpleCatalogManager api={energiesAPI} label="Énergie" itemKey="energie" icon={Zap} />}
                        {catTab === 'estado' && <SimpleCatalogManager api={estadosAPI} label="État" itemKey="estado" icon={Activity} />}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default CataloguesPage;
